import { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import { GameIcon } from '@/shared/ui/GameIcon'

export type ScoreCounterSize = 'sm' | 'md' | 'lg'

export type ScoreCounterProps = {
  value?: number
  icon?: React.ReactNode
  duration?: number
  format?: (n: number) => string
  size?: ScoreCounterSize
  className?: string
  /**
   * Cor do número em repouso. Props e não `className` do consumidor porque a
   * cor do bump vive na mesma propriedade: sobrepor por fora deixaria as duas
   * disputando especificidade, com a ordem do CSS decidindo o vencedor.
   */
  restingColorClassName?: string
  /**
   * Cor do número durante o bump — a da própria métrica exibida. Sem default
   * de propósito: uma cor fixa aqui pintaria a métrica errada em silêncio no
   * momento exato em que o participante olha. Ausente, o número mantém a cor
   * de repouso e o bump fica só no movimento.
   */
  bumpColorClassName?: string
  /** Classes extras no número — tamanho, tracking. */
  valueClassName?: string
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const SIZE = {
  sm: { icon: 18, text: 'text-sm' },
  md: { icon: 22, text: 'text-base' },
  lg: { icon: 28, text: 'text-xl' }
} as const

/**
 * Placar animado — anima count-up (ease-out-cubic) entre o valor antigo e o
 * novo, e dá um "bump" (rotate + scale) sempre que o valor *aumenta*. É o
 * principal reforço visual de "você ganhou algo" no produto — usado no slot
 * esquerdo do TopBar para o saldo de moedas. `format` customiza a formatação
 * do número exibido (default: `toLocaleString('pt-BR')`).
 *
 * @example
 * <ScoreCounter
 *   value={wallet.coins}
 *   size="sm"
 *   bumpColorClassName="text-[var(--gami-coin-color)]"
 * />
 */
export const ScoreCounter = ({
  value = 0,
  icon,
  duration = 700,
  format,
  size = 'md',
  className,
  restingColorClassName = 'text-gray-900',
  bumpColorClassName,
  valueClassName
}: ScoreCounterProps) => {
  const [display, setDisplay] = useState(value)
  const [bump, setBump] = useState(false)
  const prev = useRef(value)
  const raf = useRef<number | null>(null)

  useEffect(() => {
    const from = prev.current
    const to = value
    if (from === to) return undefined

    prev.current = to

    // Com movimento reduzido o placar salta ao valor final: o count-up e o bump
    // são justamente o tipo de animação que a preferência pede para suprimir.
    if (prefersReducedMotion()) {
      setDisplay(to)
      return undefined
    }

    let bumpTimer: number | undefined
    if (to > from) {
      setBump(true)
      bumpTimer = window.setTimeout(() => setBump(false), 480)
    }

    const startVal = from
    let startTs: number | null = null

    const step = (ts: number) => {
      if (startTs == null) startTs = ts
      const t = Math.min(1, (ts - startTs) / duration)
      setDisplay(Math.round(startVal + (to - startVal) * easeOutCubic(t)))
      if (t < 1) raf.current = requestAnimationFrame(step)
      else setDisplay(to)
    }

    raf.current = requestAnimationFrame(step)
    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current)
      if (bumpTimer != null) window.clearTimeout(bumpTimer)
    }
  }, [value, duration])

  const fmt = format ?? ((n: number) => n.toLocaleString('pt-BR'))
  const spec = SIZE[size]

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx('inline-flex items-center gap-1.5', className)}
    >
      <span
        className={clsx(
          'inline-flex shrink-0 transition-transform duration-[250ms] ease-[cubic-bezier(.34,1.56,.64,1)]',
          bump && '-rotate-[12deg] scale-[1.15]'
        )}
      >
        {icon ?? (
          <GameIcon name="star" size={spec.icon} alt="" aria-hidden />
        )}
      </span>
      <span
        className={clsx(
          'inline-block font-extrabold tabular-nums leading-none transition-[transform,color] duration-[250ms] ease-[cubic-bezier(.34,1.56,.64,1)] origin-left',
          spec.text,
          valueClassName,
          bump
            ? clsx('scale-[1.18]', bumpColorClassName ?? restingColorClassName)
            : clsx('scale-100', restingColorClassName)
        )}
      >
        {fmt(display)}
      </span>
    </div>
  )
}

ScoreCounter.displayName = 'ScoreCounter'
